/**
 * Series for the reading graph, ported from drawHistoryGraph and
 * calculatePredictions in visualizer.js: the history, a 5-point linear
 * prediction (least squares on the last 5 points, spaced by the average
 * interval), the normal band, the failure threshold at normal high + 40 % of
 * the span when the prediction trends up (low - 40 % when down), the
 * confidence band (±5 % + 3 % per step), the uncertainty band (15 % per step)
 * and the first prediction point past the threshold. Pure: the chart only
 * maps this onto uPlot.
 */
import type { NormalRange } from '../../types';

export interface SeriesPoint {
  /** ms since epoch */
  t: number;
  v: number;
}

export const PREDICTION_POINTS = 5;
export const REGRESSION_WINDOW = 5;
export const FAILURE_MARGIN = 0.4;
export const CONFIDENCE_BASE = 0.05;
export const CONFIDENCE_STEP = 0.03;
export const UNCERTAINTY_STEP = 0.15;
const Y_PADDING = 0.15;

export interface GraphModel {
  /** x values (ms) for history then prediction, chronological. */
  xs: number[];
  /** History values; null over the prediction part. */
  history: Array<number | null>;
  /** Prediction values; starts at the last history point so the lines join. */
  prediction: Array<number | null>;
  confidenceHigh: Array<number | null>;
  confidenceLow: Array<number | null>;
  uncertaintyHigh: Array<number | null>;
  uncertaintyLow: Array<number | null>;
  historyCount: number;
  normalRange: { low: number; high: number } | null;
  failureThreshold: number | null;
  warningThreshold: number | null;
  trendingUp: boolean | null;
  predictedFailure: SeriesPoint | null;
  current: SeriesPoint;
  yMin: number;
  yMax: number;
}

/** Least-squares slope and intercept over values at x = 0..n-1, with R². */
export function linearRegression(values: number[]): { slope: number; intercept: number; r2: number } {
  const n = values.length;
  if (n < 2) return { slope: 0, intercept: values[0] ?? 0, r2: 0 };
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  let sumYY = 0;
  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += values[i];
    sumXY += i * values[i];
    sumXX += i * i;
    sumYY += values[i] * values[i];
  }
  const denom = n * sumXX - sumX * sumX;
  const slope = denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  const meanY = sumY / n;
  const ssTotal = sumYY - n * meanY * meanY;
  let ssResidual = 0;
  for (let j = 0; j < n; j++) ssResidual += (values[j] - (intercept + slope * j)) ** 2;
  const r2 = ssTotal > 0 ? 1 - ssResidual / ssTotal : 0;
  return { slope, intercept, r2: Math.max(0, r2) };
}

/** `count` future points from a regression on the last REGRESSION_WINDOW points, spaced by the average interval. */
export function linearPrediction(points: SeriesPoint[], count: number = PREDICTION_POINTS): SeriesPoint[] {
  if (points.length < 2) return [];
  let totalInterval = 0;
  for (let i = 1; i < points.length; i++) totalInterval += points[i].t - points[i - 1].t;
  const avgInterval = totalInterval / (points.length - 1);
  const recent = points.slice(-REGRESSION_WINDOW).map((p) => p.v);
  const { slope, intercept } = linearRegression(recent);
  const lastIndex = recent.length - 1;
  const last = points[points.length - 1];
  const out: SeriesPoint[] = [];
  for (let i = 1; i <= count; i++) {
    out.push({ t: last.t + avgInterval * i, v: intercept + slope * (lastIndex + i) });
  }
  return out;
}

/** Threshold beyond the normal band in the trend direction; null without a range or a prediction. */
export function failureThresholdFor(
  normalRange: { low: number; high: number } | null,
  trendingUp: boolean | null,
): { failure: number; warning: number } | null {
  if (!normalRange || trendingUp === null) return null;
  const span = normalRange.high - normalRange.low;
  return trendingUp
    ? { failure: normalRange.high + span * FAILURE_MARGIN, warning: normalRange.high }
    : { failure: normalRange.low - span * FAILURE_MARGIN, warning: normalRange.low };
}

/** ±(5 % + 3 % per step) of the predicted value, the orange band. */
export function confidenceWidth(stepIndex: number): number {
  return CONFIDENCE_BASE + stepIndex * CONFIDENCE_STEP;
}

/** (step + 1) × 15 % of |value|, the widening band under the dashed line. */
export function uncertaintyWidth(stepIndex: number, value: number): number {
  return (stepIndex + 1) * UNCERTAINTY_STEP * Math.abs(value);
}

/** The first prediction point at or past the failure threshold in the trend direction. */
export function predictedFailurePoint(prediction: SeriesPoint[], threshold: number | null, trendingUp: boolean | null): SeriesPoint | null {
  if (threshold === null || trendingUp === null) return null;
  for (const p of prediction) {
    if ((trendingUp && p.v >= threshold) || (!trendingUp && p.v <= threshold)) return p;
  }
  return null;
}

function hasRange(r: NormalRange | null | undefined): r is NormalRange {
  return !!r && Number.isFinite(r.low) && Number.isFinite(r.high);
}

/** Builds the full graph model from chronological numeric points; null with fewer than 2 points. */
export function buildGraphModel(points: SeriesPoint[], normalRange?: NormalRange | null): GraphModel | null {
  if (points.length < 2) return null;
  const prediction = linearPrediction(points);
  const last = points[points.length - 1];
  const trendingUp = prediction.length ? prediction[prediction.length - 1].v > last.v : null;
  const range = hasRange(normalRange) ? { low: normalRange.low, high: normalRange.high } : null;
  const thresholds = failureThresholdFor(range, trendingUp);

  const xs: number[] = points.map((p) => p.t);
  const history: Array<number | null> = points.map((p) => p.v);
  const predictionSeries: Array<number | null> = points.map((_, i) => (i === points.length - 1 ? last.v : null));
  const confidenceHigh: Array<number | null> = points.map((_, i) => (i === points.length - 1 ? last.v : null));
  const confidenceLow = confidenceHigh.slice();
  const uncertaintyHigh = confidenceHigh.slice();
  const uncertaintyLow = confidenceHigh.slice();
  prediction.forEach((p, i) => {
    xs.push(p.t);
    history.push(null);
    predictionSeries.push(p.v);
    const c = confidenceWidth(i);
    confidenceHigh.push(p.v * (1 + c));
    confidenceLow.push(p.v * (1 - c));
    const u = uncertaintyWidth(i, p.v);
    uncertaintyHigh.push(p.v + u);
    uncertaintyLow.push(p.v - u);
  });

  const all: number[] = [...points.map((p) => p.v), ...prediction.map((p) => p.v)];
  if (range) all.push(range.low, range.high);
  if (thresholds) all.push(thresholds.failure);
  let yMin = Math.min(...all);
  let yMax = Math.max(...all);
  const span = yMax - yMin || 1;
  yMin -= span * Y_PADDING;
  yMax += span * Y_PADDING;

  return {
    xs,
    history,
    prediction: predictionSeries,
    confidenceHigh,
    confidenceLow,
    uncertaintyHigh,
    uncertaintyLow,
    historyCount: points.length,
    normalRange: range,
    failureThreshold: thresholds?.failure ?? null,
    warningThreshold: thresholds?.warning ?? null,
    trendingUp,
    predictedFailure: predictedFailurePoint(prediction, thresholds?.failure ?? null, trendingUp),
    current: last,
    yMin,
    yMax,
  };
}
